import { css, cx, keyframes } from "@emotion/css";
import { lineHeight, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useCallback, useEffect, useRef, useState } from "react";

interface LoadingMessagesProps {
    show: boolean;
    messages: string[];
}

export function LoadingMessages({ show, messages }: LoadingMessagesProps) {
    const [nextCustomMessageIndex, setNextCustomMessageIndex] = useState(0);
    const [currentMessages, setCurrentMessages] = useState<AnimatedMessage[]>([]);

    const addNextMessage = useCallback(() => {
        if (messages.length === 0) return;

        const messageIndex = nextCustomMessageIndex % messages.length;
        const message = messages[messageIndex];
        setCurrentMessages((messages) => [...messages, { message, key: `${messageIndex}` }]);
        setNextCustomMessageIndex((i) => i + 1);
    }, [messages, nextCustomMessageIndex]);

    const addNextMessageRef = useRef(addNextMessage);
    addNextMessageRef.current = addNextMessage;

    useEffect(() => {
        if (show && currentMessages.length === 0) {
            addNextMessageRef.current();
        }
    }, [currentMessages.length, show]);

    return (
        <div className={cx(styles.container, { [styles.hiddenContainer]: !show })}>
            {currentMessages.map(({ message, key }) => (
                <span
                    key={key}
                    className={styles.message}
                    onAnimationEnd={(event) => {
                        if (show && event.animationName === animations.translateDown) {
                            addNextMessage();
                        }
                        if (event.animationName === animations.fadeOut) {
                            setCurrentMessages((messages) => messages.filter((m) => m.key !== key));
                        }
                    }}
                >
                    {message}
                </span>
            ))}
        </div>
    );
}

interface AnimatedMessage {
    key: string;
    message: string;
}

const crossFadeDurationMs = 100;
const translateDurationMs = 150;
const displayMessageDurationMs = 8000;
const animateOutDurationMs = crossFadeDurationMs + translateDurationMs;
const animateOutDelayMs = animateOutDurationMs + displayMessageDurationMs;
const translateDistance = "0.5rem";

const animations = {
    fadeIn: keyframes({
        "0%": { opacity: 0 },
        "100%": { opacity: 1 },
    }),
    fadeOut: keyframes({
        "0%": { opacity: 1 },
        "100%": { opacity: 0 },
    }),
    translateUp: keyframes({
        "0%": { transform: `translateY(${translateDistance})` },
        "100%": { transform: "translateY(0)" },
    }),
    translateDown: keyframes({
        "0%": { transform: "translateY(0)" },
        "100%": { transform: `translateY(${translateDistance})` },
    }),
};

const messageLineHeight = lineHeight.xSmall;
const styles = {
    container: css({
        display: "flex",
        flexDirection: "column",
        position: "relative",
        alignItems: "center",
        height: `calc(5 * ${messageLineHeight})`,
        textAlign: "center",
        opacity: 1,
        transition: "opacity 0.2s",
    }),
    hiddenContainer: css({
        opacity: 0,
    }),
    message: css({
        position: "absolute",
        transform: `translateY(${translateDistance})`,
        opacity: 0,
        animationName: `${animations.fadeIn}, ${animations.translateUp}, ${animations.fadeOut}, ${animations.translateDown}`,
        animationDuration: `${animateOutDurationMs}ms, ${translateDurationMs}ms, ${animateOutDurationMs}ms, ${translateDurationMs}ms`,
        animationDelay: `0ms, ${animateOutDurationMs - translateDurationMs}ms, ${animateOutDelayMs}ms, ${animateOutDelayMs}ms`,
        animationTimingFunction: "linear",
        animationFillMode: "forwards",
        color: themeTokens.color.text.secondary,
        font: text.code.regular.small,
    }),
};
